import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import { Row, Col } from "react-bootstrap";
import { MdOutlineModeEditOutline, MdSupervisedUserCircle, MdAccountCircle } from "react-icons/md";

import Lead from "../../shared/Lead";
import * as API from "../../api";
import Loading from "../../shared/Loading";
import Select from "../../shared/Select";
import TextArea from "../../shared/TextArea";
import PriorityBadge from "../../shared/PriorityBadge";

import SelectAssignee from "./SelectAssignee";
import { Task, Settings } from "../../helpers/Contexts";
import Patient from "../../shared/Patient";
import FieldError from "../../shared/FieldError";

const InternalCommunicationForm = ({ task, dispatch, isCall, formHandler }) => {
  const [error, setError] = useState("");
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const settings = useContext(Settings);
  const history = useHistory();

  const TaskSchema = Yup.object().shape({
    priority: Yup.string().oneOf(["low", "medium", "high"]).nullable(),
    type: Yup.string().required("Request type is required."),
    notes: Yup.string().required("Notes is required"),
    assignee: Yup.string().required(
      "Assignee is required. Select a support group or agent to be assigned to this task."
    ),
  });

  return (
    <>
      <Formik
        initialValues={
          task.isNew
            ? {
                priority: "",
                type: "",
                assignee: "",
                notes: "",
                assignee_id: "",
                assignee_support_group_id: "",
              }
            : isCall
            ? {
                priority: "",
                type: "",
                assignee: "",
                notes: "",
                assignee_id: "",
                assignee_support_group_id: "",
              }
            : {
                priority: task.priority,
                type:
                  task.internal_communication_status && task.internal_communication_status == "pcp"
                    ? "PCP Issue"
                    : task.internal_communication_status == "billing_claims"
                    ? "Billing/Claim Issue"
                    : task.internal_communication_status == "wellportal"
                    ? "YwP Issue"
                    : task.internal_communication_status == "performance_tracking"
                    ? "Patient Issue"
                    : task.internal_communication_status == "health_plan"
                    ? "Health Plan Issue"
                    : "Other",
                assignee: task.assignee.name,
                notes: task.notes ? task.notes : "",
              }
        }
        validateOnChange={true}
        validationSchema={TaskSchema}
        onSubmit={(values) => {
          let request = {
            task_type: "internal_communication",
            status: "open",
          };
          task.patient
            ? (request = {
                ...request,
                patient_id: task.patient.id,
              })
            : null;
          if (isCall) {
            formHandler({
              task: {
                ...request,
                id: task.id,
                priority: values.priority,
                internal_communication_status: values.internal_communication_status,
                notes: values.notes,
                assignee_id: values.assignee_id && values.assignee_id,
                assignee_support_group_id:
                  values.assignee_support_group_id && values.assignee_support_group_id,
                assigner_id: settings.me.id,
                assigner_support_group_id: settings.me.support_group_id,
              },
            });
          } else {
            if (task.isNew) {
              setLoading(true);
              API.postTask({
                task: {
                  ...request,
                  priority: values.priority,
                  internal_communication_status: values.internal_communication_status,
                  notes: values.notes,
                  assignee_id: values.assignee_id && values.assignee_id,
                  assignee_support_group_id:
                    values.assignee_support_group_id && values.assignee_support_group_id,
                  assigner_id: settings.me.id,
                  assigner_support_group_id: settings.me.support_group_id,
                },
              })
                .then((res) => {
                  console.log(res);
                  setLoading(false);
                  dispatch({ type: "set_loading", payload: true });
                  history.push(`/physicians/tasks/item/${res.task.id}`);
                  setTaskSubmitted(true);
                })
                .catch((err) => {
                  console.log(err);
                  setError(err);
                });
            } else {
              task.formHandler({
                task: {
                  id: task.id,
                  priority: values.priority,
                  internal_communication_status: values.internal_communication_status,
                  notes: values.notes,
                  assigner_id: settings.me.id,
                  assigner_support_group_id: settings.me.support_group_id,
                },
              });
            }
          }
        }}
      >
        {({ values, setFieldValue, isSubmitting, errors }) => {
          function renderForm() {
            return (
              <>
                <Select label="Priority" name="priority" value={values.priority}>
                  <option value={""}> {"Choose a priority level"}</option>
                  <option value={"low"}>Low</option>
                  <option value={"medium"}>Medium</option>
                  <option value={"high"}>High</option>
                </Select>

                <label>Request Type</label>
                <select
                  id="title-case"
                  name="type"
                  value={values.type ? values.type : ""}
                  onChange={(e) => {
                    let status = settings.internal_communication_statuses.find((i) =>
                      i.label == e.target.value ? i : ""
                    );
                    setFieldValue("internal_communication_status", status.value);
                    setFieldValue("type", e.target.value);
                  }}
                >
                  {!values.type && <option value={""}>Select request type</option>}
                  {settings.internal_communication_statuses.map((option, i) => (
                    <option key={i} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {!values.type && errors.type && (
                  <div className="mt10">
                    {" "}
                    <FieldError error={"Request Type is Required"} />{" "}
                  </div>
                )}
                <TextArea
                  label="Notes"
                  value={values.notes}
                  name="notes"
                  placeholder="Please write additional notes"
                />
              </>
            );
          }

          function renderFormVals() {
            return (
              <>
                <label>
                  PRIORITY
                  {values.priority && <PriorityBadge priority={values.priority} />}
                </label>
                <label>Request Type</label>
                {values.type && values.type}
                <label> Notes</label> {values.notes && values.notes}
              </>
            );
          }

          function renderNewTaskForm() {
            return (
              <>
                <Form>
                  <Row className=" flex pad20">
                    <Col md={5} className="pad20 form">
                      <Lead message={"New Internal Communication Request"} />
                      <br />
                      <br />
                      <SelectAssignee name="assignee" setFieldValue={setFieldValue} />
                      {renderForm()}
                      <button id="btn outline" type="submit" disabled={isSubmitting}>
                        Submit Internal Communication Request
                      </button>
                    </Col>
                    <Col md={7} className={"pad20 border-container"}>
                      {loading && !taskSubmitted && <Loading message={"Creating Task "} />}
                      {task.patient && task.patient.id && <Patient patient={{ ...task.patient }} />}
                      <label className="agent">
                        {"Assignee "}{" "}
                        <span id="from">
                          {" "}
                          {values.assignee_id && values.assignee_support_group_id && (
                            <>
                              <MdAccountCircle className="task-agent" />
                              <b> {values.assignee}</b>
                            </>
                          )}
                          {!values.assignee_id && values.assignee_support_group_id && (
                            <>
                              {" "}
                              <MdSupervisedUserCircle className="task-agent" />
                              <b>{values.assignee} </b>
                            </>
                          )}
                        </span>
                      </label>

                      <hr />
                      <Lead message={"Internal Communication Request"} />
                      <br />
                      <br />

                      {renderFormVals()}
                    </Col>
                  </Row>
                </Form>
              </>
            );
          }

          function renderEditTaskForm() {
            const { taskValues, setTaskValues } = useContext(Task);

            return (
              <Row className="flex">
                {task.actions}
                <Col md={9} sm={8} className="task-cols">
                  <Row className="flex">
                    {taskValues.editing.requested && (
                      <Col md={6} className="pad20">
                        <Form>
                          <MdOutlineModeEditOutline id="pencil-icon" />
                          <Lead message={"Editing Internal Communication Request"} />
                          <br />
                          <br />

                          {renderForm()}
                          <button id="btn outline" type="submit">
                            Submit Internal Communication Request{" "}
                          </button>
                        </Form>
                      </Col>
                    )}
                    <Col md={taskValues.editing.requested ? 6 : null} className={" pad20"}>
                      <Lead message={task.label} />
                      <br />
                      <br />
                      <>
                        <label>
                          PRIORITY
                          {values.priority && (
                            <PriorityBadge
                              priority={
                                taskValues.editing.requested ? values.priority : task.priority
                              }
                            />
                          )}
                        </label>
                        <label>Request Type</label>
                        {taskValues.editing.requested
                          ? values.type && values.type
                          : settings.internal_communication_statuses.filter(
                              (i) => i.value == task.internal_communication_status
                            )[0].label}
                        <label> Notes</label>
                        {taskValues.editing.requested ? values.notes && values.notes : task.notes}
                      </>
                      ;
                      <div className="bottom-right">
                        Created at:{" "}
                        <b>
                          {new Date(task.created_at).toLocaleString([], {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </b>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            );
          }

          return (
            <>
              {(task.isNew || isCall) && renderNewTaskForm()}
              {!task.isNew && !isCall && renderEditTaskForm()}
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default InternalCommunicationForm;
